import moment from "moment";
import { JourFerieDto } from "../dto/GenApi.iNotPaie.Dto/JourFerieDto";
import { TypeJournee } from "../dto/GenApi.iNotPaie.Dto/TypeJournee";
import { ParametresInstance } from "../components/Controls/Parameters";

/**
 * Interface pour les paramètres de calcul de congé
 */
export interface CongeCalculationParams {
	dateDebut: string | Date;
	dateFin: string | Date;
	plageDateDebut: string; // "M" pour matin, "A" pour après-midi
	plageDateFin: string; // "M" pour matin, "A" pour après-midi
	listeJoursFeries: JourFerieDto[];
	compterJoursNonTravailles?: boolean; // True pour compter les week-ends et jours fériés, False pour les exclure (défaut: false)
}

/**
 * Service pour le calcul des jours de congé
 * Gère les jours fériés, plages horaires et jours ouvrables
 */
export class CongeCalculationService {
	private static readonly PLAGE_HORAIRE = { MATIN: "M", APRES_MIDI: "A" };
	private static readonly codeTypeNbHeures = "HCSS";

	/**
	 * Vérifie si le congé est à calculer en heures ou en jours
	 * @param typeConge
	 * @returns
	 */
	public static estCongeCalculHeures(typeConge: string): boolean {
		return typeConge != this.codeTypeNbHeures;
	}

	/**
	 * Calcule la date maximale autorisée pour une absence (exercice + 1 an)
	 * @returns La date maximale autorisée ou null si non calculable
	 */
	private static getDateMaxAutorisee(): moment.Moment | null {
        const periodeActive = ParametresInstance.societeCourante?.PeriodeActive;

		if (!periodeActive)
			return null;

		return moment(periodeActive).add(1, "year");
	}

	/**
	 * Vérifie si la date de fin de l'absence respecte la règle : date fin ≤ exercice + 1 an
	 * Règle alignée sur le backend (AbsencesService.cs)
	 * @param dateFin - La date de fin de l'absence
	 * @returns True si la date de fin est valide, False sinon
	 */
	public static estDateFinValide(dateFin: Date): boolean {
		const dateFinMoment = moment(dateFin);
		const dateMaxAutorisee = this.getDateMaxAutorisee();

		// Si on ne peut pas déterminer la date max, on laisse le backend valider
		if (!dateMaxAutorisee) {
			return true;
		}

		return dateFinMoment.isSameOrBefore(dateMaxAutorisee, "day");
	}

	/**
	 * Calcule le nombre de jours de congé en tenant compte des jours fériés et des plages horaires
	 * Logique alignée sur GetDureeAbsence du backend (AbsencesService.cs)
	 * @param params - Paramètres de calcul
	 * @returns Nombre de jours de congé calculé
	 */
	public static calculerNombreJours(params: CongeCalculationParams): number {
		if (!params.dateDebut || !params.dateFin || !params.plageDateDebut || !params.plageDateFin) return 0;

		const momentDebut = moment(params.dateDebut);
		const momentFin = moment(params.dateFin);
		if (momentFin.isBefore(momentDebut)) return 0;

		const compterJoursNonTravailles = params.compterJoursNonTravailles === true;
		let nombreJours = 0;

		for (const dateCourante = momentDebut.clone(); dateCourante.isSameOrBefore(momentFin, "day"); dateCourante.add(1, "day")) {
			if (!compterJoursNonTravailles && !this.estJourOuvrable(dateCourante)) continue;

			const estPremierJour = dateCourante.isSame(momentDebut, "day");
			const estDernierJour = dateCourante.isSame(momentFin, "day");

			// Par défaut journée complète
			let valeurMatin = 0.5;
			let valeurAprem = 0.5;

			// Premier jour : pas de matin si commence l'après-midi
			if (estPremierJour && params.plageDateDebut !== this.PLAGE_HORAIRE.MATIN) valeurMatin = 0;

			// Dernier jour : pas d'après-midi si finit le matin
			if (estDernierJour && params.plageDateFin !== this.PLAGE_HORAIRE.APRES_MIDI) valeurAprem = 0;

			// Jours fériés
			if (!compterJoursNonTravailles) {
				if (this.estPlageFeriee(dateCourante, params.listeJoursFeries, TypeJournee.Matin)) valeurMatin = 0;
				if (this.estPlageFeriee(dateCourante, params.listeJoursFeries, TypeJournee.ApresMidi)) valeurAprem = 0;
			}

			nombreJours += valeurMatin + valeurAprem;
		}

		return nombreJours;
	}

	/**
	 * Vérifie si une plage est fériée
	 * @param date La date à vérifier
	 * @param joursFeries La liste des jours fériés
	 * @param plage Le matin ou l'après midi
	 * @returns True si la plage est fériée, false sinon
	 */
	private static estPlageFeriee(date: moment.Moment, joursFeries: JourFerieDto[], plage: TypeJournee): boolean {
		return joursFeries.some(jf => date.isSame(moment(jf.Date), "day") &&
			(jf.TypeJournee === TypeJournee.JourneeEntiere || jf.TypeJournee === plage)
		);
	}

	/**
	 * Vérifie si un jour est ouvrable (n'est pas un dimanche ou un samedi, selon config)
	 * @param date - La date à vérifier
	 * @returns True si c'est un jour ouvrable, False sinon
	 */
	private static estJourOuvrable(date: moment.Moment): boolean {
		return date.day() !== 0 && (date.day() !== 6 || ParametresInstance.societeCourante?.JourneesAcquis !== 25);
	}
}



// WEBPACK FOOTER //
// ./src/services/CongeCalculationService.ts